import { IoIosArrowBack, IoIosArrowForward, IoIosClose } from "react-icons/io";
import useTranslator from "../../hooks/useTranslator";
import { convertCurrencyFormat } from "../../helper/common";

// keyboard support for the clickable divs (divs, so the global button
// styles don't apply)
export const onEnterKey = (e) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    e.currentTarget.click();
  }
};

/**
 * Side panel shell shared by the payslip / tax / other income panels.
 * crumbs: [{ label, onClick }] — the last one is the current page.
 * All styles are scoped under the ii- prefix (style.css).
 */
const IncomePanel = ({
  isOpen,
  onClose,
  onBack,
  title,
  crumbs = [],
  total = 0,
  actionLabel,
  onAction,
  children,
}) => {
  const { _t } = useTranslator();
  if (!isOpen) return null;

  return (
    <div className="ii-scrim" onClick={onClose}>
      <aside
        className="ii-drawer"
        role="dialog"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ii-ph">
          <div className="ii-ph-top">
            <div
              role="button"
              tabIndex={0}
              className="ii-ib"
              onKeyDown={onEnterKey}
              onClick={onBack ?? onClose}
              aria-label={_t("back")}
            >
              <IoIosArrowBack size={17} />
            </div>
            <div className="ii-ph-title">{title}</div>
            <div
              role="button"
              tabIndex={0}
              className="ii-ib"
              onKeyDown={onEnterKey}
              onClick={onClose}
              aria-label={_t("close")}
            >
              <IoIosClose size={24} />
            </div>
          </div>
          {crumbs.length > 0 && (
            <nav className="ii-crumbs">
              {crumbs.map((c, i) =>
                i === crumbs.length - 1 ? (
                  <span key={i} className="ii-crumb-current">
                    {c.label}
                  </span>
                ) : (
                  <span key={i} className="ii-crumb-wrap">
                    <span
                      role="button"
                      tabIndex={0}
                      className="ii-crumb"
                      onKeyDown={onEnterKey}
                      onClick={c.onClick}
                    >
                      {c.label}
                    </span>
                    <IoIosArrowForward size={12} />
                  </span>
                )
              )}
            </nav>
          )}
        </div>
        <div className="ii-pb">{children}</div>
        <div className="ii-pf">
          <div className="ii-pf-t">
            {_t("totalWeUse")}
            <b>€{convertCurrencyFormat(total)}</b>
          </div>
          <div
            role="button"
            tabIndex={0}
            className="ii-btn"
            onKeyDown={onEnterKey}
            onClick={onAction ?? onClose}
          >
            {actionLabel ?? _t("done")}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default IncomePanel;

const CheckBox = () => (
  <span className="ii-box">
    <svg viewBox="0 0 12 12" aria-hidden="true">
      <path
        d="M2.5 6.2l2.3 2.3 4.7-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const ViewLink = ({ url, className, children }) =>
  url ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </a>
  ) : null;

/**
 * Document row of step 2: checkbox | title + meta + Details | View
 */
export const IncomePanelDoc = ({
  isOn,
  title,
  badges,
  meta,
  onToggle,
  onDetails,
  viewUrl,
}) => {
  const { _t } = useTranslator();
  return (
    <div className={`ii-doc ${isOn ? "ii-on" : ""}`}>
      <div
        role="button"
        tabIndex={0}
        className="ii-cb"
        onKeyDown={onEnterKey}
        onClick={onToggle}
        aria-pressed={isOn}
        aria-label={title}
      >
        <CheckBox />
      </div>
      <div
        role="button"
        tabIndex={0}
        className="ii-doc-b"
        onKeyDown={onEnterKey}
        onClick={onDetails}
      >
        <span className="ii-doc-tx">
          <span className="ii-doc-t">
            {title}
            {badges}
          </span>
          {meta && <span className="ii-doc-m">{meta}</span>}
        </span>
        <span className="ii-doc-go">
          {_t("details")}
          <IoIosArrowForward size={13} />
        </span>
      </div>
      <ViewLink url={viewUrl} className="ii-vw">
        {_t("view")}
      </ViewLink>
    </div>
  );
};

/**
 * Figure row of step 3 (checkbox). info = shown for information only.
 */
export const IncomePanelOption = ({
  isOn,
  label,
  sub,
  value,
  onToggle,
  info = false,
  radio = false,
}) => (
  <div
    role={info ? undefined : radio ? "radio" : "button"}
    tabIndex={info ? undefined : 0}
    className={`ii-opt ${isOn ? "ii-on" : ""} ${info ? "ii-info" : ""}`}
    onKeyDown={info ? undefined : onEnterKey}
    onClick={info ? undefined : onToggle}
    aria-pressed={info || radio ? undefined : isOn}
    aria-checked={radio ? isOn : undefined}
  >
    {radio ? <span className="ii-radio" /> : <CheckBox />}
    <span className="ii-opt-l">
      {label}
      {sub && <small>{sub}</small>}
    </span>
    <span className="ii-opt-v">{value}</span>
  </div>
);

/**
 * "Taken from <document>" box at the top of step 3.
 */
export const IncomePanelSource = ({ title, viewUrl }) => {
  const { _t } = useTranslator();
  return (
    <div className="ii-src">
      <span className="ii-src-tx">
        {_t("takenFrom")}
        <b>{title}</b>
      </span>
      <ViewLink url={viewUrl} className="ii-chip">
        {_t("openDocument")}
      </ViewLink>
    </div>
  );
};
